import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { FC, ReactNode } from 'react';
import type { RichTextContent, Sys } from 'src/types/contentful-sdk-compat';
import {
  Box,
  Column,
  Container,
  Row,
  Section,
  Stack,
} from 'src/components-v2/Layout';
import { ConditionalWrapper } from 'src/components-v2/Helpers';
import { CmsMediaSwitch } from 'src/components-v2/Media/CmsMediaSwitch';
import { CmsAssetProps } from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch.types';
import { RichTextSwitch } from 'src/containers';
import type { HeroElement } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { CmsThemes } from 'src/lib/constants';
import { isBlackBackgroundColor, makeImageSource } from 'src/lib/utils';
import ThemeProvider from 'threads5/themes/theme-provider';

export interface FormWrapperProps {
  children: ReactNode;
  backgroundColor?: string;
  mode?: string;
  body?: RichTextContent | null;
  asset?: CmsAssetProps | null;
  sys?: Sys;
  id: string;
  component?: HeroElement;
}

export const FormWrapper: FC<FormWrapperProps> = ({
  children,
  sys,
  backgroundColor,
  mode: cmsMode,
  body,
  asset,
  id,
  component = 'section',
}) => {
  const isBackgroundColorBlack = isBlackBackgroundColor(backgroundColor);
  const textColorStyle = {
    color: isBackgroundColorBlack ? 'white' : 'inherit',
  };
  const cmsTheme = useCmsTheme();
  const isWeb3 = cmsTheme === CmsThemes.WEB3;
  const mode = useCmsMode({ mode: cmsMode, backgroundColor });
  const componentTheme = useComponentTheme(mode);
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  const headingColorSx = isWeb3
    ? { color: 'web3/text/heading' }
    : textColorStyle;

  const contentNodes = {
    h1: {
      component: 'h1',
      variant: 'h1',
      fieldId: 'leftColumn',
      sx: isWeb3 ? headingColorSx : { ...textColorStyle, fontSize: '4.0rem' },
    },
    h2: {
      component: 'h2',
      variant: 'h2',
      fieldId: 'leftColumn',
      sx: headingColorSx,
    },
    h3: {
      component: 'h3',
      variant: 'h3',
      fieldId: 'leftColumn',
      sx: headingColorSx,
    },
    h6: {
      component: 'h6',
      variant: 'h6',
      className: 'eyebrow-heading',
      fieldId: 'leftColumn',
      sx: isWeb3
        ? { color: 'web3/text/eyebrow' }
        : {
            color: isBackgroundColorBlack ? 'white' : 'var(--black800)',
            fontSize: { lg: '1.2rem' },
            letterSpacing: { lg: '1.25px' },
          },
    },
    p: {
      component: 'p',
      variant: 'p2',
      fieldId: 'leftColumn',
      sx: isWeb3 ? { color: 'web3/text/paragraph' } : textColorStyle,
    },
  };

  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(asset);

  const background = componentTheme.palette[backgroundColor] || backgroundColor;

  return (
    // scoped to web 3.0 so existing web 2.0 pages keep the ambient theme,
    // the 'white' BackgroundContext default, and the legacy backgroundColor css
    <ConditionalWrapper
      condition={isWeb3}
      wrapper={(children) => {
        return <ThemeProvider theme={componentTheme}>{children}</ThemeProvider>;
      }}
    >
      <Section
        component={component}
        id={id}
        backgroundColor={isWeb3 ? backgroundColor : undefined}
        sx={isWeb3 ? { background } : { backgroundColor }}
        {...inspectorProps({ fieldId: 'internalTitle' })}
      >
        <Container>
          <Row direction={{ xs: 'column-reverse', lg: 'row' }}>
            <Column
              xs={22}
              xsOffset={1}
              sm={22}
              smOffset={1}
              lg={isWeb3 ? 9 : 8}
              lgOffset={2}
              sx={isWeb3 ? undefined : { p: '0' }}
            >
              <Stack
                sx={{
                  '& div[class*="form__disclaimer"] p': {
                    mb: 0,
                  },
                  '& input': {
                    borderRadius: '8px',
                  },
                  '& i + span': {
                    fontSize: '1.4rem',
                  },
                  '& fieldset': {
                    '& div:has(input)': {
                      borderRadius: '8px',
                    },
                  },
                  '& .button-group': {
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: '1.8rem', sm: '1.2rem', lg: '2.6rem' },
                  },
                  // for decorative text
                  '& p:has(.decorative-text), h1:has(.decorative-text)': {
                    mb: { xs: 1, md: 2 },
                    // for icons
                    '& svg': {
                      mt: { xs: '0.2rem', md: '0.4rem' },
                      zoom: { xs: 1, md: 1.2 }, // we use zoom because it works for all icons
                    },
                  },
                  ...(!isWeb3 && {
                    pt: {
                      lg: '10rem',
                    },
                    pb: {
                      lg: '6rem',
                    },
                  }),
                }}
              >
                <Box
                  sx={{
                    '& p:nth-last-of-type(1):empty': {
                      display: 'none',
                    },
                    ...(isWeb3
                      ? // values from the Web Design System Figma, Hero Form Asset Left-Right
                        {
                          mb: { xs: '3.2rem', lg: '4.8rem' },
                          '& .eyebrow-heading, .decorative-text': {
                            mb: '1.6rem',
                            fontSize: '1.4rem',
                            fontWeight: 700,
                            letterSpacing: '0.125rem',
                          },
                          '& h1': {
                            mb: { xs: '1.6rem', lg: '2.4rem' },
                            fontSize: { xs: '4.0rem', lg: '6.0rem' },
                            lineHeight: { xs: '114%', lg: '108%' },
                            letterSpacing: '-0.2rem',
                          },
                          '& h2, & h3': {
                            mb: { xs: '1.6rem', lg: '2.4rem' },
                          },
                          '& p': {
                            mb: '1.6rem',
                            fontSize: '2rem',
                            lineHeight: { xs: 1.4, lg: 1.5 },
                          },
                          // the gap to the form comes from the Box margin
                          '& p:last-of-type': {
                            mb: 0,
                          },
                        }
                      : {
                          mb: '1.6rem',
                          '& .eyebrow-heading, .decorative-text': {
                            mb: '1.6rem',
                            fontSize: '1.2rem',
                          },
                          '& h1': {
                            mb: '1.6rem',
                            fontSize: '4.0rem',
                            lineHeight: { lg: '120%' },
                            maxWidth: { sm: '556px', lg: '537px' },
                          },
                          '& h2, & h3': {
                            mb: '1.6rem',
                            maxWidth: { sm: '556px', lg: '537px' },
                          },
                          '& p': {
                            mb: '1.6rem',
                            maxWidth: { sm: '556px', lg: '468px' },
                          },
                        }),
                  }}
                >
                  <RichTextSwitch
                    sys={sys}
                    content={body}
                    nodes={contentNodes}
                  />
                </Box>

                {children}
              </Stack>
            </Column>
            <Column
              xs={isWeb3 ? 24 : 22}
              xsOffset={isWeb3 ? 0 : 1}
              sm={22}
              smOffset={1}
              lg={isWeb3 ? 10 : 13}
              lgOffset={1}
              sx={
                isWeb3
                  ? undefined
                  : {
                      p: '0',
                      mb: { xs: '3.2rem', sm: '4.8rem', lg: '0' },
                    }
              }
            >
              <Box
                sx={
                  isWeb3
                    ? {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        aspectRatio: { xs: 'unset', lg: '1' },
                        mb: { xs: '40px', lg: '0' },
                      }
                    : {
                        mr: {
                          lg: '-4rem',
                          xxl: 'calc(((100vw - 1730px) / -2) - 40px)',
                        },
                        minHeight: { lg: '100%' },
                        aspectRatio: { lg: '925 / 863' },
                        overflow: 'hidden',
                        borderRadius: { xs: '0.8rem', sm: '0.8rem', lg: '0' },
                        borderTopLeftRadius: { lg: '1.2rem' },
                        borderBottomLeftRadius: { lg: '1.2rem' },
                      }
                }
              >
                {asset && (
                  <CmsMediaSwitch
                    asset={asset}
                    queryString={
                      isWeb3
                        ? { xl: `?fm=webp&w=1496&q=60` }
                        : formImageQueryString
                    }
                    sources={
                      isWeb3
                        ? {
                            xs: `${smImgSrc}?fm=webp&w=320&q=60 1x, ${smImgSrc}?fm=webp&w=640&q=60 2x`,
                            sm: `${smImgSrc}?fm=webp&w=513&q=60 1x, ${smImgSrc}?fm=webp&w=1026&q=60 2x`,
                            md: `${mdImgSrc}?fm=webp&w=863&q=60 1x, ${mdImgSrc}?fm=webp&w=1726&q=60 2x`,
                            lg: `${lgImgSrc}?fm=webp&w=541&q=60 1x, ${lgImgSrc}?fm=webp&w=1082&q=60 2x`,
                            xl: `${lgImgSrc}?fm=webp&w=748&q=60 1x, ${lgImgSrc}?fm=webp&w=1496&q=60 2x`,
                          }
                        : undefined
                    }
                    sx={{
                      display: 'block',
                      width: '100%',
                      objectFit: 'cover',
                      ...(isWeb3
                        ? {
                            objectPosition: 'left center',
                            borderRadius: {
                              xs: '12px',
                              sm: '24px',
                              md: '30px',
                            },
                            height: 'auto',
                          }
                        : {
                            height: { xs: 'auto', lg: '100%' },
                          }),
                    }}
                  />
                )}
              </Box>
            </Column>
          </Row>
        </Container>
      </Section>
    </ConditionalWrapper>
  );
};

const BASE = '?fm=webp&q=60';
const formImageQueryString = {
  xs: `${BASE}&w=800`,
  sm: `${BASE}&w=1234`,
  md: `${BASE}&w=1902`,
  lg: `${BASE}&w=2430`,
};
